import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Modal, ModalHeader, ModalBody, Card, CardBody, Collapse, Row, Col, Badge, Button } from 'reactstrap';
import NoteModal from './NoteModal';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const SubjectDetailsModal = ({ isOpen, toggle, subjectId }) => {
  const [subjectDetails, setSubjectDetails] = useState(null);
  const [openRecordings, setOpenRecordings] = useState({});
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedRecordingId, setSelectedRecordingId] = useState(null);

  useEffect(() => {
    if (isOpen && subjectId) {
      fetchSubjectDetails();
    } else {
      // Clear data when modal is closed
      setSubjectDetails(null);
      setOpenRecordings({});
    }
  }, [isOpen, subjectId]);

  const fetchSubjectDetails = async () => {
    try {
      const response = await axios.post(`${BASE_URL}web/getSubjectDetails`, { subjectId });
      setSubjectDetails(response.data);
    } catch (error) {
      console.error("Error fetching subject details:", error);
    }
  };

  const toggleRecording = (recordingId) => {
    setOpenRecordings(prev => ({ ...prev, [recordingId]: !prev[recordingId] }));
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short'
    });
  };
  
  const calculateDuration = (start, end) => {
    // For ongoing recordings or cancelled recordings
    if (!start || start > end) return 'Cancelled';
    
    const duration = end - start;
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);
    
    return parts.join(' ');
  };
  const handleAddNote = (recordingId) => {
    setSelectedRecordingId(recordingId);
    setShowNoteModal(true);
  };

  const handleNoteSaved = () => {
    // Optionally, you can refresh the subject details here
    fetchSubjectDetails();
  };



  const renderRecordingBar = (startTime, endTime, totalDuration) => {
    const width = ((endTime - startTime) / totalDuration) * 100;
    return (
      <div className="position-relative" style={{ height: '20px', width: '100%', backgroundColor: '#e9ecef', marginTop: '10px' }}>
        <div
          className="position-absolute"
          style={{
            width: `${width}%`,
            height: '100%',
            backgroundColor: '#007bff'
          }}
        />
      </div>
    );
  };

  const formatHeight = (height, heightUnit) => {
    if (heightUnit === "ft_in") {
      const feet = Math.floor(height / 12);
      const inches = height % 12;
      return `${feet}' ${inches}"`;
    } else {
      return `${height} ${heightUnit}`;
    }
  };

  if (!subjectDetails) return null;

  const validBloodSugarLevels = subjectDetails.bloodSugarLevels.filter(level => level.bloodSugar !== null);
  const uncancelledRecordings = subjectDetails.recordings.filter(recording => !recording.recordingCanceled);

  const cardStyle = {
    boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)',
    transition: '0.3s',
    borderRadius: '5px',
  };

  const cardBodyStyle = {
    padding: '1.25rem',
  };

  const summaryCardStyle = {
    ...cardStyle,
    backgroundColor: '#fffaf0', // Floral white background
    border: '2px solid #ffd700', // Gold border
  };

  const recordingCardStyle = {
    ...cardStyle,
    backgroundColor: '#f5f5f5', // White smoke background
    border: '1px solid #a9a9a9', // Dark gray border
  };

  const mobileDeviceCardStyle = {
    ...cardStyle,
    backgroundColor: '#f0f8ff', // Light blue background
    border: '1px solid #4682b4', // Steel blue border
  };

  const cameraDeviceCardStyle = {
    ...cardStyle,
    backgroundColor: '#f0fff0', // Light green background
    border: '1px solid #228b22', // Forest green border
  };

  const bloodSugarCardStyle = {
    ...cardStyle,
    backgroundColor: '#fff0f5', // Lavender blush background
    border: '1px solid #ff69b4', // Hot pink border
  };


  const badgeStyle = {
    fontSize: '0.7rem',
    padding: '0.5em 0.7em',
    marginRight: '0.7em',
    marginLeft: '0.7em',
  };

  const bloodPressureCardStyle = {
    ...cardStyle,
    backgroundColor: '#e6f2ff', // Light blue background
    border: '1px solid #4d94ff', // Blue border
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="xl">
        <ModalHeader toggle={toggle}>Subject Details</ModalHeader>
        <ModalBody>
          <Card className="mb-4" style={summaryCardStyle}>
            <CardBody style={cardBodyStyle}>
              <Row>
                <Col md={3}>
                  <strong>Name:</strong> {subjectDetails.subjectName}
                </Col>
                <Col md={3}>
                  <strong>Age:</strong> {new Date().getFullYear() - subjectDetails.birthYear}
                </Col>
                <Col md={3}>
                  <strong>Height:</strong> {formatHeight(subjectDetails.height, subjectDetails.heightUnit)}
                </Col>
                <Col md={3}>
                  <strong>Weight:</strong> {subjectDetails.weight} {subjectDetails.weightUnit}
                </Col>
              </Row>
            </CardBody>
          </Card>

          <h5 className="mb-3">Recordings ({uncancelledRecordings.length}/{subjectDetails.recordings.length})</h5>
          {subjectDetails.recordings.map((recording) => (
            <Card key={recording.recordingId} className="mb-3" style={recordingCardStyle}>
              <CardBody style={cardBodyStyle}>
                <div className="d-flex justify-content-between align-items-center" onClick={() => toggleRecording(recording.recordingId)} style={{ cursor: 'pointer' }}>
                  <div>
                    <p>{recording.recordingId}</p>
                    <strong>{formatDate(recording.recordingCanceled ? recording.createdAt : recording.recordingStarted)}</strong>
                    {recording.recordingCanceled ?
                      <Badge color="danger" style={badgeStyle} className="ml-3">CANCELLED</Badge> :
                      <span className="ml-3">({calculateDuration(recording.recordingStarted, recording.recordingEnded)})</span>
                    }
                  </div>
                  <div>
                    <Badge color="primary" style={badgeStyle}>Mobile: {recording.recordingDevices.mobileDevices.length}</Badge>
                    <Badge color="success" style={badgeStyle}>Camera: {recording.recordingDevices.cameraDevices.length}</Badge>
                    <span style={{ fontSize: '1.5em', marginLeft: '0.7em' }}>{openRecordings[recording.recordingId] ? '▲' : '▼'}</span>
                  </div>
                </div>

                <Collapse isOpen={openRecordings[recording.recordingId]}>
                  <div className="mt-4">
                    <h6 className="mb-3">Mobile Devices</h6>
                    {recording.recordingDevices.mobileDevices.map((device, index) => (
                      <Card key={index} className="mb-3" style={mobileDeviceCardStyle}>
                        <CardBody style={cardBodyStyle}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div>
                              <strong>Device {index + 1}: {device.deviceId}</strong>
                            </div>
                            <div>
                              <Badge color="secondary" style={badgeStyle}>Camera: {device.camera}</Badge>
                              <Badge color={device.bleEnabled ? "info" : "warning"} style={badgeStyle}>
                                Bluetooth: {device.bleEnabled ? "On" : "Off"}
                              </Badge>
                            </div>
                          </div>
                          {!device.cancelled && !device.dropped && !device.failedReason && (
                            <>
                              <div className="mb-2">
                                <strong>Duration: </strong>
                                {calculateDuration(device.recordingStarted, device.recordingEnded)}
                              </div>
                              {renderRecordingBar(device.recordingStarted, device.recordingEnded, recording.recordingEnded - recording.recordingStarted)}
                            </>
                          )}
                          <div className="mt-2">
                            {device.cancelled && <Badge color="danger" style={badgeStyle}>Cancelled</Badge>}
                            {device.dropped && <Badge color="warning" style={badgeStyle}>Dropped</Badge>}
                            {device.failedReason && <Badge color="danger" style={badgeStyle}>{device.failedReason}</Badge>}
                          </div>
                        </CardBody>
                      </Card>
                    ))}

                    {recording.recordingDevices.cameraDevices.length > 0 && (
                      <>
                        <h6 className="mb-3">Camera Devices</h6>
                        {recording.recordingDevices.cameraDevices.map((device, index) => (
                          <Card key={index} className="mb-3" style={cameraDeviceCardStyle}>
                            <CardBody style={cardBodyStyle}>
                              <div className="mb-2">
                                <strong>Camera {index + 1}: {device.deviceId}</strong>
                              </div>
                              <div className="mb-2">
                                <strong>Duration: </strong>
                                {calculateDuration(recording.recordingStarted, recording.recordingEnded)}
                              </div>
                              {renderRecordingBar(recording.recordingStarted, recording.recordingEnded, recording.recordingEnded - recording.recordingStarted)}
                            </CardBody>
                          </Card>
                        ))}
                      </>
                    )}
                  </div>

                  {recording.notes && recording.notes.length > 0 && (
                    <div className="mt-3">
                      <h6>Notes:</h6>
                      <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #ccc', borderRadius: '4px', padding: '10px' }}>
                        {recording.notes.map((note, index) => (
                          <div key={index} className="mb-2">
                            <small>{formatDate(note.createdAt)}</small>
                            <p className="mb-1">{note.note}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Collapse>
                <Button
                  color="info"
                  size="sm"
                  onClick={() => handleAddNote(recording.recordingId)}
                  className="mt-2"
                >
                  Add Note
                </Button>


              </CardBody>
            </Card>
          ))}

          {validBloodSugarLevels.length > 0 && (
            <>
              <h5 className="mt-4 mb-3">Blood Sugar Levels ({validBloodSugarLevels.length})</h5>
              {validBloodSugarLevels.map((level, index) => (
                <Card key={index} className="mb-3" style={bloodSugarCardStyle}>
                  <CardBody style={cardBodyStyle}>
                    <p className="mb-2"><strong>Time:</strong> {formatDate(level.createdAt)}</p>
                    <p className="mb-2"><strong>Sugar Level:</strong> {level.bloodSugar}</p>
                    {level.lastMealTime && <p className="mb-2"><strong>Last Meal:</strong> {level.lastMealTime.replace('_', ' ')}</p>}
                    {level.recordingId && <p className="mb-0"><strong>After Recording:</strong> Yes</p>}
                  </CardBody>
                </Card>
              ))}
            </>
          )}

          {subjectDetails.bloodPressure && subjectDetails.bloodPressure.length > 0 && (
            <>
              <h5 className="mt-4 mb-3">Blood Pressure Readings ({subjectDetails.bloodPressure.length})</h5>
              {subjectDetails.bloodPressure.map((reading, index) => (
                <Card key={index} className="mb-3" style={bloodPressureCardStyle}>
                  <CardBody style={cardBodyStyle}>
                    <p className="mb-2"><strong>Time:</strong> {formatDate(reading.createdAt)}</p>
                    <p className="mb-2"><strong>Systolic:</strong> {reading.systolic} mmHg</p>
                    <p className="mb-0"><strong>Diastolic:</strong> {reading.diastolic} mmHg</p>
                  </CardBody>
                </Card>
              ))}
            </>
          )}

        </ModalBody>
      </Modal>
      <NoteModal
        isOpen={showNoteModal}
        toggle={() => setShowNoteModal(false)}
        recordingId={selectedRecordingId}
        onNoteSaved={handleNoteSaved}
        title="Add Note"
      />
    </>
  );
};

export default SubjectDetailsModal;